import { useSyncExternalStore } from 'react';
import { ConfigProvider } from 'antd';
import AppRouter from '../router';
import ErrorBoundary from '../error-boundary';
import {
  token,
  semantic,
  radius,
  fontFamily,
  motion,
} from '../../styles/tokens';
import '../../styles/global.css';

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/** The OS "reduce motion" setting, live: antd's own animations follow it. */
const subscribeReducedMotion = (onChange: () => void) => {
  if (typeof window.matchMedia !== 'function') return () => {};
  const query = window.matchMedia(REDUCED_MOTION);
  query.addEventListener?.('change', onChange);
  return () => query.removeEventListener?.('change', onChange);
};
const readReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia(REDUCED_MOTION).matches;

function App() {
  const reducedMotion = useSyncExternalStore(
    subscribeReducedMotion,
    readReducedMotion,
    () => false,
  );
  return (
    <div className="App">
      {/*
        The single override here used to be `colorPrimary: '#2B3467'` -- a navy
        that appears nowhere in the design system, and which the login page did
        not use either: that page hardcoded a *different* navy, #1b2b6b. Two
        brand colours, neither of them the brand's.

        colorPrimary is ink-900 rather than the accent. antd spends its primary
        on every focus ring, link, selected row, active tab and checked control,
        and the brand rules cap the accent at roughly 5% of a surface. Seeding
        the accent would blow that cap everywhere without anyone choosing to, so
        the accent is applied deliberately -- Button type="primary" is ink, and
        accent is reserved for the places the system names.
      */}
      {/*
        wave: antd's click ripple, a Material gesture that marks every button as
        antd's. Press feedback is a scale on touch-down instead (global.css).
        motion: antd's animations obey the OS "reduce motion" setting, which
        they did not; motionEaseOut gives every dropdown, popover and modal the
        strong ease-out the rest of the app uses.
      */}
      <ConfigProvider
        wave={{ disabled: true }}
        theme={{
          token: {
            motion: !reducedMotion,
            motionEaseOut: motion.easeOut,
            motionDurationFast: motion.durationFast,
            colorPrimary: token.ink900,
            colorLink: token.accent600,
            colorText: token.ink900,
            colorTextSecondary: token.gray600,
            colorTextDescription: token.gray600,
            colorBorder: token.border,
            colorBorderSecondary: token.border,
            colorBgLayout: token.mist50,
            colorBgContainer: token.white,
            colorFillQuaternary: token.gray100,
            colorSuccess: token.success500,
            // Not mapped in tier 1, so antd's default #ff4d4f was still
            // reaching every error surface -- including the job list's
            // "Closed" badge, which sat next to a tokenised green.
            colorError: semantic.error,
            colorWarning: semantic.warning,
            borderRadius: radius.md,
            borderRadiusLG: radius.lg,
            borderRadiusSM: radius.sm,
            fontFamily,
          },
          components: {
            // Flat white fill, 1px inset border, no drop shadow at rest. The
            // lift variant exists in the system but only to separate a card
            // from a busy background, which is not the resting state here.
            Card: { boxShadowTertiary: 'none' },
            Button: { primaryShadow: 'none', defaultShadow: 'none' },
            Table: { headerBg: token.mist50, headerColor: token.gray600 },
          },
        }}
      >
        <ErrorBoundary>
          <AppRouter />
        </ErrorBoundary>
      </ConfigProvider>
    </div>
  );
}

export default App;
